/**
 * Marker pixel sizes, in one place.
 *
 * These used to be inline literals: the structure pin's 24/40 pair was typed
 * twice inside useMarkerLayer.web.ts alone, and again in leafletHelpers.ts and
 * Map.native.tsx, kept in sync only by comments pointing at each other. Web
 * and native draw the same shapes from the same paths (src/theme/categoryIcons)
 * and should draw them at the same size, so the numbers live together.
 *
 * Sizes came down across the board on Aug 25 2026 at the client's ask ("can we
 * make icons a little smaller and clearer when they overlap"). The observation
 * dot was the worst offender: at 28px with a 3px border and a 4px halo it
 * covered ~42px, wider than the 24px property pin it was drawn on top of.
 */

// Observation (post) markers.
export const POST_DOT_SIZE = 22;
export const POST_DOT_BORDER = 2;
export const POST_DOT_HALO = 3;
export const POST_DELTA_SIZE = 24;
export const POST_GLYPH_SIZE = 12;

// Structure (property) pins.
export const STRUCTURE_PIN_SIZE = 20;
export const STRUCTURE_PIN_SIZE_SELECTED = 32;
export const STRUCTURE_PIN_BORDER = 2;
export const STRUCTURE_PIN_BORDER_SELECTED = 3;

/**
 * Cluster bubble diameter by member count. Size alone carries density: the
 * numeric label was dropped from the main map per client direction ("they
 * clutter the look"), and observation clusters follow the same rule.
 */
export function clusterBubbleSize(count: number): number {
  return count < 20 ? 32 : count < 100 ? 38 : 44;
}

/**
 * Property cluster house size by member count: 40 / 48 / 56 px. Larger than
 * the observation bubbles on purpose, since the client asked for "a big
 * house" (Oct 14 2026). Size still carries density; no numeric label.
 */
export function propertyClusterSize(count: number): number {
  return count < 20 ? 40 : count < 100 ? 48 : 56;
}

/**
 * z-index tiers. Leaflet resolves equal offsets by latitude and then by
 * insertion order, so anything sharing a tier needs its own tie-break.
 */
export const Z_STRUCTURE = 0;
export const Z_POST = 600;
export const Z_SELECTED = 1000;

/**
 * Spread within a tier, so overlapping markers stack the same way on every
 * render instead of following whatever order the data arrived in.
 */
export const Z_LATITUDE_SPREAD = 200;
